
import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { dlPush } from './utils/dataLayer';
import { PRODUCTS } from './utils/products';

const NavBar = ({ cartCount }) => (
  <nav className="nav">
    <Link className="brand" to="/">GTM Demo</Link>
    <NavLink to="/" end>Home</NavLink>
    <NavLink to="/products">Products</NavLink>
    <NavLink to="/blog">Blog</NavLink>
    <NavLink to="/video">Video</NavLink>
    <NavLink to="/contact">Contact</NavLink>
    <NavLink className="right" to="/cart">Cart ({cartCount})</NavLink>
  </nav>
);

const usePageView = () => {
  const location = useLocation();
  useEffect(() => {
    dlPush('page_view', {
      page_path: location.pathname + location.search,
      page_title: document.title
    });
  }, [location]);
};

const Home = () => {
  // Element visibility demo
  useEffect(() => {
    const el = document.getElementById('observe-me');
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        dlPush('element_visible', { element_id: 'observe-me', element_name: 'HeroCallout' });
      }
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className="container">
      <div className="card" id="observe-me">
        <h1>Welcome to the GTM Training Playground</h1>
        <p className="muted">Scroll to trigger element visibility. Click buttons and links to fire events.</p>
        <div className="flex">
          <Link to="/products"><button>Shop Gadgets</button></Link>
          <a href="https://www.example.com" className="right" onClick={(e)=>{
            // Outbound link click demo
            dlPush('outbound_link_click', { url: 'https://www.example.com', link_text: 'Example.com' });
          }}>Outbound link →</a>
        </div>
      </div>

      <div className="hr" />

      <div className="grid">
        {PRODUCTS.map(p => (
          <div key={p.id} className="card">
            <h3>{p.name}</h3>
            <p className="muted">${p.price.toFixed(2)}</p>
            <Link to={`/products/${p.id}`}><button className="ghost">View</button></Link>
          </div>
        ))}
      </div>
    </div>
  );
};

const Products = ({ add }) => {
  const navigate = useNavigate();
  return (
    <div className="container">
      <h2>Products <span className="badge">Demo</span></h2>
      <div className="grid">
        {PRODUCTS.map(p => (
          <div key={p.id} className="card">
            <h3>{p.name}</h3>
            <p className="muted">${p.price.toFixed(2)}</p>
            <div className="flex">
              <button onClick={()=>{
                add(p);
                dlPush('add_to_cart', { item_id: p.id, item_name: p.name, price: p.price, quantity: 1 });
              }}>Add to cart</button>
              <button className="ghost" onClick={()=>{
                dlPush('view_item', { item_id: p.id, item_name: p.name, price: p.price });
                navigate(`/products/${p.id}`);
              }}>Details</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProductDetail = ({ add }) => {
  const { id } = useParams();
  const product = useMemo(()=> PRODUCTS.find(p=>p.id === id), [id]);
  useEffect(()=>{
    if (product) dlPush('view_item', { item_id: product.id, item_name: product.name, price: product.price });
  }, [product]);
  if (!product) return <div className="container"><p>Not found.</p></div>;
  return (
    <div className="container">
      <div className="card">
        <h2>{product.name}</h2>
        <p className="muted">${product.price.toFixed(2)}</p>
        <button onClick={()=>{
          add(product);
          dlPush('add_to_cart', { item_id: product.id, item_name: product.name, price: product.price, quantity: 1 });
        }}>Add to cart</button>
      </div>
    </div>
  );
};

const Blog = () => (
  <div className="container">
    <div className="grid">
      {Array.from({length: 6}).map((_,i)=>(
        <article key={i} className="card">
          <h3>Blog Post #{i+1}</h3>
          <p className="muted">Practice scroll, clicks, and element visibility. Use GTM to track article engagement.</p>
          <button onClick={()=> dlPush('blog_cta_click', { post_id: i+1, post_title: `Blog Post #${i+1}` })}>Read more</button>
        </article>
      ))}
    </div>
  </div>
);

const Video = () => (
  <div className="container">
    <div className="card">
      <h2>Video Demo</h2>
      <p className="muted">Click play to simulate a video play event.</p>
      <button onClick={()=> dlPush('video_play', { video_id: 'yt_demo_1', video_title: 'Demo Video' })}>Play video ▶</button>
      <div style={{height:20}} />
      <div className="small muted">In a real setup, you could use the YouTube iFrame API and listen for <code>onStateChange</code> to push events.</div>
    </div>
  </div>
);

const Contact = () => {
  const [name,setName] = useState('');
  const [email,setEmail] = useState('');
  const [msg,setMsg] = useState('');
  const onSubmit = (e) => {
    e.preventDefault();
    dlPush('form_submit', { form_id: 'contact', form_name: 'Contact Form' });
    alert('Submitted! (Simulated)');
    setName(''); setEmail(''); setMsg('');
  };
  return (
    <div className="container">
      <div className="card">
        <h2>Contact</h2>
        <form onSubmit={onSubmit}>
          <label htmlFor="name">Name</label>
          <input id="name" value={name} onChange={e=>setName(e.target.value)} placeholder="Jane Doe" />
          <label htmlFor="email">Email</label>
          <input id="email" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="jane@example.com" />
          <label htmlFor="msg">Message</label>
          <textarea id="msg" value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Hello!" />
          <div style={{height:12}} />
          <button type="submit">Send</button>
        </form>
      </div>
    </div>
  );
};

const Cart = ({ items, remove, clear }) => {
  const value = items.reduce((sum,i)=>sum + i.price, 0);
  return (
    <div className="container">
      <h2>Your Cart</h2>
      <div className="card">
        {items.length === 0 ? <p className="muted">Cart is empty.</p> : (
          <>
            {items.map((i,idx)=>(
              <div key={idx} className="flex" style={{padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,.08)'}}>
                <div>{i.name}</div>
                <div className="right">${i.price.toFixed(2)}</div>
                <button className="ghost" onClick={()=> remove(idx)}>Remove</button>
              </div>
            ))}
            <div className="flex" style={{marginTop:12}}>
              <strong>Total</strong>
              <div className="right"><strong>${value.toFixed(2)}</strong></div>
            </div>
            <div className="flex" style={{marginTop:12}}>
              <Link to="/checkout"><button>Checkout</button></Link>
              <button className="ghost right" onClick={()=> clear()}>Clear</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Checkout = ({ items, clear }) => {
  const navigate = useNavigate();
  const cartValue = items.reduce((sum,i)=>sum + i.price, 0);
  useEffect(()=>{
    dlPush('begin_checkout', {
      cart_value: cartValue,
      items: items.map(i => ({ item_id: i.id, item_name: i.name, price: i.price, quantity: 1 }))
    });
  }, []); // fire once on load

  return (
    <div className="container">
      <div className="card">
        <h2>Checkout</h2>
        <p className="muted">This is a fake checkout. Click purchase to simulate a conversion.</p>
        <button onClick={()=>{
          const tx = 'tx_' + Math.random().toString(36).slice(2,8);
          dlPush('purchase', {
            transaction_id: tx,
            value: cartValue,
            items: items.map(i => ({ item_id: i.id, item_name: i.name, price: i.price, quantity: 1 }))
          });
          clear();
          navigate('/');
          alert('Purchase simulated!');
        }}>Complete Purchase</button>
      </div>
    </div>
  );
};

export default function App(){
  usePageView();
  const [cart, setCart] = useState([]);
  const add = (p) => setCart(c => [...c, p]);
  const remove = (idx) => setCart(c => c.filter((_,i)=>i!==idx));
  const clear = () => setCart([]);

  return (
    <div>
      <NavBar cartCount={cart.length} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products add={add} />} />
        <Route path="/products/:id" element={<ProductDetail add={add} />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/video" element={<Video />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/cart" element={<Cart items={cart} remove={remove} clear={clear} />} />
        <Route path="/checkout" element={<Checkout items={cart} clear={clear} />} />
        <Route path="*" element={<div className="container"><p>Not found.</p></div>} />
      </Routes>
      <footer className="container small muted" style={{textAlign:'center', paddingBottom:32}}>
        <div>Train GTM on this site. Open DevTools Console to see your <code>dataLayer</code> pushes.</div>
      </footer>
    </div>
  );
}
